import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { LineChart } from '@mui/x-charts/LineChart';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import walletApi from '../../api/wallet';
import { getItemWithExpiry } from './../../utils/cache';
import { formatCurrency } from '../../utils/Currency';
import { calculateMargin } from '../../utils/Chart';
import { useCurrency } from '../../contexts/CurrencyContext';

const PERIOD_CONFIG = {
    '1w': { days: 7, granularity: 'daily', label: '1S' },
    '1m': { days: 30, granularity: 'daily', label: '1M' },
    '3m': { days: 90, granularity: 'weekly', label: '3M' },
    '6m': { days: 180, granularity: 'weekly', label: '6M' },
    '1y': { days: 365, granularity: 'weekly', label: '1A' },
    '2y': { days: 730, granularity: 'monthly', label: '2A' },
    'max': { days: 3650, granularity: 'yearly', label: 'MAX' },
};

const HomeChart = ({ embedded = false, showValues: externalShowValues, currency: externalCurrency }) => {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedPeriod, setSelectedPeriod] = useState(() => {
        const isMobile = window.innerWidth < 768;
        return isMobile ? '1w' : '1m';
    });
    const [latestTotal, setLatestTotal] = useState(null);
    const [internalShowValues, setInternalShowValues] = useState(false);
    const { currency: globalCurrency } = useCurrency();

    const currency = embedded && externalCurrency ? externalCurrency : globalCurrency;
    const showValues = embedded ? externalShowValues : internalShowValues;
    const setShowValues = setInternalShowValues;

    const periodConfig = useMemo(() => PERIOD_CONFIG[selectedPeriod], [selectedPeriod]);
    const timeGranularity = periodConfig.granularity;
    const periodDays = periodConfig.days;

    const generateLastNDays = (days) => {
        const dates = [];
        const now = new Date();

        // Validação para evitar períodos muito longos que podem causar problemas
        const maxDays = Math.min(days, 365);

        for (let i = maxDays - 1; i >= 0; i--) {
            const date = new Date();
            date.setDate(now.getDate() - i);
            const formattedDate = `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
            dates.push(formattedDate);
        }
        return dates;
    };

    const generateLastNWeeks = (days) => {
        const weeks = Math.ceil(days / 7);
        const weekDates = [];
        const now = new Date();

        const dayOfWeek = now.getDay();
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - dayOfWeek);

        for (let i = weeks - 1; i >= 0; i--) {
            const weekStart = new Date(startOfWeek);
            weekStart.setDate(startOfWeek.getDate() - (i * 7));

            const formattedWeek = `${String(weekStart.getDate()).padStart(2, '0')}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${weekStart.getFullYear()}`;
            weekDates.push(formattedWeek);
        }
        return weekDates;
    };

    const generateLastNMonths = (days) => {
        const months = Math.ceil(days / 30);
        const monthDates = [];
        const now = new Date();
        for (let i = months - 1; i >= 0; i--) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const formattedMonth = `01-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
            monthDates.push(formattedMonth);
        }
        return monthDates;
    };

    const generateLastNYears = (days) => {
        const years = Math.ceil(days / 365);
        const yearDates = [];
        const now = new Date();
        for (let i = years - 1; i >= 0; i--) {
            const year = now.getFullYear() - i;
            yearDates.push(`01-01-${year}`);
        }
        return yearDates;
    };

    const processChartData = (data, granularity) => {
        const groupedData = {};

        if (data.length === 0) {
            // Garantir que datas ausentes sejam adicionadas mesmo sem dados iniciais
            const addMissingDates = (dates) => {
                dates.forEach((date) => {
                    if (!groupedData[date]) {
                        groupedData[date] = { date, totalInvested: 0, currentValue: 0 };
                    }
                });
            };

            if (granularity === 'daily') {
                addMissingDates(generateLastNDays(periodDays));
            } else if (granularity === 'weekly') {
                addMissingDates(generateLastNWeeks(periodDays));
            } else if (granularity === 'monthly') {
                addMissingDates(generateLastNMonths(periodDays));
            } else if (granularity === 'yearly') {
                addMissingDates(generateLastNYears(periodDays));
            }

            return Object.values(groupedData).sort((a, b) => {
                const dateA = new Date(a.date.split('-').reverse().join('-'));
                const dateB = new Date(b.date.split('-').reverse().join('-'));
                return dateA - dateB;
            });
        }

        data.forEach((item) => {
            const date = new Date(item.PositionDate);
            let key;

            switch (granularity) {
                case 'weekly':
                    const dayOfWeek = date.getDay();
                    const startOfWeek = new Date(date);
                    startOfWeek.setDate(date.getDate() - dayOfWeek);
                    key = `${String(startOfWeek.getDate()).padStart(2, '0')}-${String(startOfWeek.getMonth() + 1).padStart(2, '0')}-${startOfWeek.getFullYear()}`;
                    break;
                case 'monthly':
                    key = `01-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
                    break;
                case 'yearly':
                    key = `01-01-${date.getFullYear()}`;
                    break;
                default:
                    key = `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
            }

            if (
                !groupedData[key] ||
                new Date(item.ConsolidateDate) > new Date(groupedData[key].consolidateDate || 0)
            ) {
                groupedData[key] = {
                    date: key,
                    totalInvested: parseFloat(item.TotalInvested[currency]) || 0,
                    currentValue: parseFloat(item.CurrentValue[currency]) || 0,
                    consolidateDate: item.ConsolidateDate
                };
            }
        });

        const addMissingDates = (dates) => {
            dates.forEach((date) => {
                if (!groupedData[date]) {
                    groupedData[date] = { date, totalInvested: 0, currentValue: 0 };
                }
            });
        };

        if (granularity === 'daily') {
            addMissingDates(generateLastNDays(periodDays));
        } else if (granularity === 'weekly') {
            addMissingDates(generateLastNWeeks(periodDays));
        } else if (granularity === 'monthly') {
            addMissingDates(generateLastNMonths(periodDays));
        } else if (granularity === 'yearly') {
            addMissingDates(generateLastNYears(periodDays));
        }

        return Object.values(groupedData).sort((a, b) => {
            const dateA = new Date(a.date.split('-').reverse().join('-'));
            const dateB = new Date(b.date.split('-').reverse().join('-'));
            return dateA - dateB;
        });
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            try {
                const now = new Date();
                const startDate = new Date();
                startDate.setDate(now.getDate() - periodDays);

                const formattedStartDate = startDate.toISOString().split('T')[0];
                const formattedEndDate = now.toISOString().split('T')[0];

                let response = null;
                let rawData;

                const shouldUseCache = selectedPeriod === '1w' || selectedPeriod === '1m';

                if (shouldUseCache) {
                    response = getItemWithExpiry('CONSOLIDATED_ASSETS_BY_TYPE');
                }

                if (!response) {
                    const url = `/positions/asset-class?StartDate=${formattedStartDate}&EndDate=${formattedEndDate}&View=${timeGranularity}`;
                    response = await walletApi.get(url);
                    if (response.status === 204) {
                        const defaultData = processChartData([], timeGranularity);
                        setChartData(defaultData);
                        setLatestTotal(null);
                        return;
                    }
                    rawData = response.data.Totals;
                } else {
                    rawData = JSON.parse(response).result;
                }

                if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
                    const defaultData = processChartData([], timeGranularity);
                    setChartData(defaultData);
                    setLatestTotal(null);
                    return;
                }

                const processedData = processChartData(rawData, timeGranularity);
                setChartData(processedData);

                const latestData = rawData[0];
                if (latestData) {
                    const invested = parseFloat(latestData.TotalInvested[currency]);
                    const current = parseFloat(latestData.CurrentValue[currency]);

                    if (!isNaN(invested) && !isNaN(current) && invested > 0) {
                        const variation = ((current - invested) / invested) * 100;
                        setLatestTotal({
                            date: latestData.consolidate_date,
                            totalInvested: invested,
                            currentValue: current,
                            variation: variation.toFixed(2),
                        });
                    } else {
                        setLatestTotal(null);
                    }
                } else {
                    setLatestTotal(null);
                }
            } catch (err) {
                setError('Todo mundo erra e dessa vez foram nossos servidores. :(');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedPeriod, currency]);

    const renderChart = () => (
        <>
            {loading && <Typography>Carregando dados...</Typography>}
            {error && <Typography color="error">{error}</Typography>}
            {!loading && !error && chartData.length > 0 && (
                <LineChart
                    xAxis={[
                        {
                            dataKey: 'date',
                            scaleType: 'point',
                        },
                    ]}
                    yAxis={[{
                        min: (() => {
                            const values = chartData.flatMap(d => [d.totalInvested || 0, d.currentValue || 0]);
                            const minVal = Math.min(...values);
                            const maxVal = Math.max(...values);
                            if (minVal === 0 && maxVal === 0) return 0;
                            const range = maxVal - minVal;
                            return Math.max(0, minVal - range * 0.1);
                        })(),
                        max: (() => {
                            const values = chartData.flatMap(d => [d.totalInvested || 0, d.currentValue || 0]);
                            const maxVal = Math.max(...values);
                            if (maxVal === 0) return 100;
                            const minVal = Math.min(...values);
                            const range = maxVal - minVal;
                            return maxVal + range * 0.1;
                        })(),
                        tickNumber: 8,
                    }]}
                    series={[
                        {
                            dataKey: 'totalInvested',
                            label: `Total Investido (${currency})`,
                            valueFormatter: (value) => formatCurrency(currency, value || 0),
                        },
                        {
                            dataKey: 'currentValue',
                            label: `Valor Atual (${currency})`,
                            valueFormatter: (value, { dataIndex }) => {
                                const current = value || 0;
                                const invested = chartData[dataIndex]?.totalInvested || 0;
                                const profit = current - invested;
                                const variation = invested > 0 ? ((profit / invested) * 100) : 0;
                                const sign = profit >= 0 ? '+' : '';
                                return `${formatCurrency(currency, current)} → ${sign}${variation.toFixed(2)}% (${sign}${formatCurrency(currency, profit)})`;
                            },
                        },
                    ]}
                    dataset={chartData}
                    height={embedded ? (isSmall ? 300 : 410) : 400}
                    grid={{ horizontal: true }}
                    margin={calculateMargin(chartData)}
                />
            )}
            {!loading && !error && chartData.length === 0 && null}
        </>
    );

    if (embedded) {
        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        mb: 2,
                        maxWidth: '100%',
                        overflowX: 'auto',
                        pb: 0.5,
                        '&::-webkit-scrollbar': { height: 4 },
                    }}
                >
                    <ToggleButtonGroup
                        value={selectedPeriod}
                        exclusive
                        onChange={(_, value) => value && setSelectedPeriod(value)}
                        size="small"
                        sx={{ flexShrink: 0 }}
                    >
                        {Object.entries(PERIOD_CONFIG).map(([key, config]) => (
                            <ToggleButton
                                key={key}
                                value={key}
                                sx={{
                                    px: { xs: 1.25, sm: 2 },
                                    '&.Mui-selected': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                        color: theme.palette.primary.main,
                                        fontWeight: 600,
                                    },
                                }}
                            >
                                {config.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
                <Box sx={{ flex: 1 }}>
                    {renderChart()}
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.light,
                    0.15
                )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 60%)`,
                boxShadow: theme.shadows[2],
                mb: 4,
            }}
        >
            <Typography variant="h5" component="div" gutterBottom>
                Dashboard de Evolucao Patrimonial
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 2,
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <ToggleButtonGroup
                    value={selectedPeriod}
                    exclusive
                    onChange={(_, value) => value && setSelectedPeriod(value)}
                    size="small"
                >
                    {Object.entries(PERIOD_CONFIG).map(([key, config]) => (
                        <ToggleButton
                            key={key}
                            value={key}
                            sx={{
                                px: 2.5,
                                '&.Mui-selected': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                },
                            }}
                        >
                            {config.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <IconButton onClick={() => setShowValues((prev) => !prev)}>
                    {showValues ? <Visibility /> : <VisibilityOff />}
                </IconButton>
            </Box>
            {latestTotal && (
                <Box sx={{ mb: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Box
                            sx={{
                                flex: '1 1 30%',
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                background: alpha(theme.palette.info.main, 0.08),
                                boxShadow: theme.shadows[1],
                                minWidth: '150px',
                            }}
                        >
                            <Typography variant="h6" gutterBottom>
                                Total Investido
                            </Typography>
                            <Typography>
                                {showValues
                                    ? new Intl.NumberFormat('pt-BR', {
                                        style: 'currency',
                                        currency: currency,
                                    }).format(latestTotal.totalInvested)
                                    : '--'}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                flex: '1 1 30%',
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
                                background: alpha(theme.palette.warning.main, 0.12),
                                boxShadow: theme.shadows[1],
                                minWidth: '150px',
                            }}
                        >
                            <Typography variant="h6" gutterBottom>
                                Valor Atual
                            </Typography>
                            <Typography>
                                {showValues
                                    ? new Intl.NumberFormat('pt-BR', {
                                        style: 'currency',
                                        currency: currency,
                                    }).format(latestTotal.currentValue)
                                    : '--'}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                flex: '1 1 30%',
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${alpha(
                                    latestTotal.variation >= 0 ? theme.palette.success.main : theme.palette.error.main,
                                    0.35
                                )}`,
                                background: alpha(
                                    latestTotal.variation >= 0 ? theme.palette.success.main : theme.palette.error.main,
                                    0.12
                                ),
                                boxShadow: theme.shadows[1],
                                minWidth: '150px',
                            }}
                        >
                            <Typography variant="h6" gutterBottom>
                                Variação
                            </Typography>
                            <Typography
                                sx={{
                                    color: latestTotal.variation >= 0 ? '#388e3c' : '#d32f2f',
                                    fontWeight: 'bold',
                                    marginTop: 1,
                                }}
                            >
                                {showValues ? (
                                    <>
                                        {latestTotal.variation >= 0 ? '+' : ''}
                                        {latestTotal.variation}%
                                    </>
                                ) : (
                                    '--'
                                )}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}
            <Box
                sx={{
                    height: 400,
                    width: '100%',
                    backgroundColor: theme.palette.background.paper,
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                    boxShadow: theme.shadows[1],
                }}
            >
                {renderChart()}
            </Box>
        </Box>
    );
};

export default HomeChart;